import LockIcon from '@mui/icons-material/Lock';
import { Box, Zoom } from '@mui/material';
import classNames from 'classnames';
import * as React from 'react';
import { memo, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { FlightTurnaroundDto, Resource, ResourceType } from '../../../../../../../backend';
import {
    getTurnaroundActualOccupation,
    getTurnaroundScheduledOccupation,
    isTurnaroundScheduledOrActual,
} from '../../../../RmsContent.utils';
import { useRmsGanttValue } from '../../../../RmsContext';
import { ganttOneMinuteInMilliSeconds, ganttPxPerMs } from '../../../gantt.constants';
import { getProposedAllocationByTurnaround, getResourceName } from '../../../gantt.utils';
import { CustomTooltip } from '../CustomTooltip';
import { DisplayCtotData } from '../DisplayCtotData';
import { DisplayTurnaroundData } from '../DisplayTurnaroundData';
import {
    StyledBox,
    StyledCtotEndBox,
    StyledEtdEndBox,
    StyledIndicatorBox,
    StyledInfoIcon,
    StyledLineBox,
    StyledMinTimeBetweenUseBox,
    StyledPadlockBox,
    StyledTurnaroundBox,
} from './TurnaroundBox.style';
import { TurnaroundZoneBox } from './TurnaroundZoneBox';
import { TurnaroundZoneData } from './TurnaroundZoneData';

export const TurnaroundBox = memo(function TurnaroundBox({
    turnaround,
    resource,
    marginValue,
}: {
    turnaround?: FlightTurnaroundDto;
    resource?: Resource;
    marginValue?: number;
}) {
    const proposedAllocation = useRmsGanttValue('proposedAllocation');
    const nbUpdateAllocations = useRmsGanttValue('nbUpdateAllocations');
    const resourceType = useRmsGanttValue('resourceType');
    const day = useRmsGanttValue('day');
    const dayBounds = useRmsGanttValue('dayBounds');
    const turnaroundOccupation = getTurnaroundActualOccupation(turnaround);
    const scheduledTurnaroundOccupation = getTurnaroundScheduledOccupation(turnaround);
    const isTurnaroundOccupationIncoherent =
        (resourceType === ResourceType.PARKING || resourceType === ResourceType.TERMINAL) &&
        turnaround?.arrivalFlight?.sibt &&
        turnaround?.departureFlight?.sobt
            ? turnaround.arrivalFlight.sibt > turnaround.departureFlight.sobt
            : false;
    const rmsFilters = useRmsGanttValue('rmsFilters');
    const minTimeBetweenUse = resource
        ? resource.minTimeBetweenUse * ganttOneMinuteInMilliSeconds * ganttPxPerMs * 2
        : 0;

    const isScheduledOrActual = isTurnaroundScheduledOrActual(
        day,
        turnaround.considerArrivalFlight
            ? turnaround.arrivalFlight?.actualResourceOccupations?.[resourceType]
            : turnaround.departureFlight?.actualResourceOccupations?.[resourceType],
        scheduledTurnaroundOccupation,
    );

    const ctot =
        nbUpdateAllocations === 0 &&
        turnaround?.considerDepartureFlight &&
        (resourceType === ResourceType.PARKING || resourceType === ResourceType.BOARDING_GATE) &&
        turnaround?.departureFlight?.ctot &&
        turnaroundOccupation?.occupationEnd
            ? turnaround.departureFlight.ctot - turnaroundOccupation.occupationEnd.time
            : null;

    const etd =
        nbUpdateAllocations === 0 &&
        resourceType === ResourceType.BOARDING_GATE &&
        turnaround?.considerDepartureFlight &&
        turnaround.departureFlight?.actualResourceOccupations?.[ResourceType.PARKING]?.occupationEnd &&
        turnaroundOccupation?.occupationEnd
            ? turnaround.departureFlight.actualResourceOccupations[ResourceType.PARKING].occupationEnd.time -
              turnaroundOccupation.occupationEnd.time
            : null;

    const isProposition = useMemo(() => {
        return (
            proposedAllocation &&
            getProposedAllocationByTurnaround(proposedAllocation, turnaround) &&
            getProposedAllocationByTurnaround(proposedAllocation, turnaround).resource !== getResourceName(turnaround)
        );
    }, [proposedAllocation, turnaround]);

    const isCtotUnderLimit = ctot <= 5 * ganttOneMinuteInMilliSeconds;
    const ctotClassNames = classNames({
        ctotRed: !isScheduledOrActual && isCtotUnderLimit,
        ctotYellow: !isScheduledOrActual && !isCtotUnderLimit,
        ctotGray: isScheduledOrActual,
    });

    const addMinTimeBetweenUseIndicator =
        resource &&
        resource.minTimeBetweenUse > 0 &&
        turnaroundOccupation?.occupationEnd &&
        turnaroundOccupation.occupationEnd.time <= dayBounds?.to;

    if (!turnaroundOccupation) {
        return null;
    }

    return (
        <StyledTurnaroundBox
            marginvalue={marginValue}
            sx={isProposition ? { alignItems: 'center' } : { alignItems: 'end' }}
        >
            <Box position={'relative'} display={'flex'}>
                <CustomTooltip
                    title={<DisplayTurnaroundData turnaround={turnaround} />}
                    TransitionComponent={Zoom}
                    placement="bottom"
                    className={classNames({
                        ctotGray: isScheduledOrActual,
                    })}
                >
                    <StyledBox
                        width={
                            (Math.min(turnaroundOccupation.occupationEnd?.time, dayBounds?.to) -
                                turnaroundOccupation.occupationStart?.time) *
                            ganttPxPerMs *
                            2
                        }
                        className={classNames({ proposition: isProposition })}
                    >
                        <TurnaroundZoneData turnaround={turnaround} />
                        <TurnaroundZoneBox
                            turnaround={turnaround}
                            resourceType={resourceType}
                            isProposition={isProposition}
                        />
                    </StyledBox>
                </CustomTooltip>
                {rmsFilters.viewModeScheduled === 'scheduled' && isTurnaroundOccupationIncoherent && (
                    <CustomTooltip
                        title={<FormattedMessage id={'turnaround.viewModeScheduled.incoherent'} />}
                        TransitionComponent={Zoom}
                        className={'ctotGray'}
                    >
                        <StyledInfoIcon fontSize={'small'} />
                    </CustomTooltip>
                )}
                {ctot != null && (
                    <StyledIndicatorBox>
                        {ctot > 0 && (
                            <>
                                <StyledLineBox
                                    width={`${Math.trunc(ctot * ganttPxPerMs * 2 - 2)}px`}
                                    className={ctotClassNames}
                                    zIndex={-1}
                                />

                                <StyledCtotEndBox className={ctotClassNames} />
                            </>
                        )}

                        <CustomTooltip
                            title={<DisplayCtotData ctotTime={turnaround?.departureFlight?.ctot} />}
                            TransitionComponent={Zoom}
                            className={'ctotTooltip ' + ctotClassNames}
                        >
                            <StyledPadlockBox className={ctotClassNames}>
                                <LockIcon sx={{ height: '11px', width: '11px' }} />
                            </StyledPadlockBox>
                        </CustomTooltip>
                    </StyledIndicatorBox>
                )}
                {turnaround?.considerDepartureFlight && (
                    <StyledIndicatorBox>
                        {etd > 0 && (
                            <>
                                <StyledLineBox
                                    width={`${Math.trunc(etd * ganttPxPerMs * 2 - 2)}px`}
                                    className={'etdGreen'}
                                />

                                <StyledEtdEndBox />
                            </>
                        )}
                    </StyledIndicatorBox>
                )}
            </Box>
            {addMinTimeBetweenUseIndicator && (
                <StyledMinTimeBetweenUseBox>
                    <Box height={'2px'} width={minTimeBetweenUse - 1} bgcolor={'white'} zIndex={1} />
                    <Box height={'20px'} width={'2px'} bgcolor={'white'} zIndex={1} />
                </StyledMinTimeBetweenUseBox>
            )}
        </StyledTurnaroundBox>
    );
});
